import React, { type ReactNode, useMemo, useRef, useState, useCallback } from "react";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import type { Dashboard } from "../../../Dashboards/hook/api/dashboard";
import DashboardTab from "./DashboardTab";
import SeeMoreMenu from "./SeeMoreMenu";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { AddCircleLine, ArrowRightDoubleLine, More2Fill } from "@ui/IconV2/react";
import useObserverCallback from "@ui/lib/hooks/useObserverCallback";
import { SUMMARY_ID } from "../utils";
import { useLocation, useParams } from "react-router";

interface DashboardTabState extends Dashboard {
  visibility: boolean;
}

export default function DashboardTabs({
  projectId,
  publishedDashboards,
  canEdit,
  contextualMenu,
  dashboardPopup,
}: {
  projectId: string;
  publishedDashboards: Dashboard[];
  canEdit: boolean;
  contextualMenu: ReactNode;
  dashboardPopup: ReactNode;
}) {
  const location = useLocation();
  const { dashboardId } = useParams();
  const dashboardTabListRef = useRef<HTMLDivElement | null>(null);
  const shadowSeeMoreButtonRef = useRef<HTMLSpanElement | null>(null);
  const shadowContextualMenuButtonRef = useRef<HTMLSpanElement | null>(null);
  const shadowAddDashboardButtonRef = useRef<HTMLSpanElement | null>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [visibleTabs, setVisibleTabs] = useState<DashboardTabState[]>(() =>
    publishedDashboards.map(d => ({ ...d, visibility: true })),
  );

  const isOverflow = useMemo(() => visibleTabs.some(t => !t.visibility), [visibleTabs]);
  const isActiveDashboardOverflow = useMemo(
    () =>
      visibleTabs.some(
        tab =>
          !tab.visibility &&
          (tab.id === SUMMARY_ID ? location.pathname.endsWith("summary") : dashboardId === tab.id),
      ),
    [dashboardId, location.pathname, visibleTabs],
  );

  const updateTabsVisibility = useCallback(() => {
    const containerWidth = dashboardTabListRef.current?.offsetWidth ?? 0;
    const seeMoreButtonWidth = shadowSeeMoreButtonRef.current?.offsetWidth ?? 0;
    const contextualMenuButtonWidth = shadowContextualMenuButtonRef.current?.offsetWidth ?? 0;
    const addDashboardButtonWidth = shadowAddDashboardButtonRef.current?.offsetWidth ?? 0;
    const addButtonWidth = canEdit ? addDashboardButtonWidth : 0;

    const computeNextTabs = (reservedButtonsWidth: number) => {
      const availableTabsWidth = Math.max(containerWidth - reservedButtonsWidth, 0);
      let totalWidth = 0;

      return publishedDashboards.map((tab, index) => {
        const tabWidth = tabRefs.current[index]?.offsetWidth ?? 0;
        totalWidth += tabWidth;
        return { ...tab, visibility: totalWidth <= availableTabsWidth };
      });
    };

    const nextTabsWithoutSeeMore = computeNextTabs(addButtonWidth);
    const shouldReserveSeeMore = nextTabsWithoutSeeMore.some(tab => !tab.visibility);
    const fixedButtonsWidth = addButtonWidth + (shouldReserveSeeMore ? seeMoreButtonWidth : 0);

    const nextTabsWithoutContextualMenu = shouldReserveSeeMore
      ? computeNextTabs(fixedButtonsWidth)
      : nextTabsWithoutSeeMore;
    const shouldReserveContextualMenu = nextTabsWithoutContextualMenu.some(
      tab =>
        !tab.visibility &&
        (tab.id === SUMMARY_ID ? location.pathname.endsWith("summary") : dashboardId === tab.id),
    );

    const nextTabs = shouldReserveContextualMenu
      ? computeNextTabs(fixedButtonsWidth + contextualMenuButtonWidth)
      : nextTabsWithoutContextualMenu;

    const hasChanged =
      nextTabs.length !== visibleTabs.length ||
      nextTabs.some(
        (t, i) => t.visibility !== visibleTabs[i]?.visibility || t.id !== visibleTabs[i]?.id,
      );

    if (hasChanged) {
      setVisibleTabs(nextTabs);
    }
  }, [canEdit, dashboardId, location.pathname, publishedDashboards, visibleTabs]);

  useObserverCallback(
    () => {
      tabRefs.current = publishedDashboards.map((_, i) => tabRefs.current[i] ?? null);
      updateTabsVisibility();
    },
    [publishedDashboards, updateTabsVisibility],
    dashboardTabListRef,
    { fireOnInit: true, debounced: false },
  );

  return (
    <div className={dashboardTabListWrapper} ref={dashboardTabListRef}>
      <div className={shadowTabList}>
        {visibleTabs.map((tab, index) => {
          const isSummary = tab.id === SUMMARY_ID;
          const isActive = isSummary
            ? location.pathname.endsWith("summary")
            : dashboardId === tab.id;

          return (
            <DashboardTab
              ref={el => (tabRefs.current[index] = el)}
              key={tab.id}
              projectId={projectId}
              dashboardId={tab.id}
              dashboardName={tab.title}
              isActive={isActive && tab.visibility}
              contextualMenu={
                <ButtonIconV2>
                  <More2Fill size="m" />
                </ButtonIconV2>
              }
            />
          );
        })}
      </div>

      <div className={shadowButtons}>
        <span ref={shadowSeeMoreButtonRef}>
          <ButtonIconV2>
            <ArrowRightDoubleLine size="m" />
          </ButtonIconV2>
        </span>
        <span ref={shadowContextualMenuButtonRef}>
          <ButtonIconV2>
            <More2Fill size="m" />
          </ButtonIconV2>
        </span>
        {canEdit ? (
          <span ref={shadowAddDashboardButtonRef}>
            <ButtonIconV2>
              <AddCircleLine size="m" />
            </ButtonIconV2>
          </span>
        ) : null}
      </div>

      <div className={tabList}>
        {visibleTabs
          .filter(f => f.visibility)
          .map(tab => {
            const isSummary = tab.id === SUMMARY_ID;
            const isActive = isSummary
              ? location.pathname.endsWith("summary")
              : dashboardId === tab.id;

            return (
              <DashboardTab
                key={tab.id}
                projectId={projectId}
                dashboardId={tab.id}
                dashboardName={tab.title}
                isActive={isActive && tab.visibility}
                contextualMenu={contextualMenu}
              />
            );
          })}
      </div>

      <div className={buttons}>
        {isOverflow && <SeeMoreMenu projectId={projectId} visibleTabs={visibleTabs} />}
        {isActiveDashboardOverflow ? contextualMenu : null}
        {canEdit ? dashboardPopup : null}
      </div>
    </div>
  );
}

//#region CSS
const dashboardTabListWrapper = css`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;

  button {
    flex-shrink: 0;
    flex-grow: 0;
  }
`;

const shadowTabList = css`
  display: flex;
  align-items: center;
  overflow: hidden;
  position: absolute;
  visibility: hidden;
`;

const shadowButtons = css`
  display: flex;
  align-items: center;
  position: absolute;
  visibility: hidden;
`;

const tabList = css`
  display: flex;
  align-items: center;
  overflow: hidden;

  &:empty {
    padding-left: ${spacings.xxs};
  }
`;

const buttons = css`
  height: 40px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-shrink: 0;
  flex-grow: 0;
`;
//#endregion CSS
